import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { Receipt } from '@budgetmonkey/shared'
import { formatDate } from '@/lib/dates'
import { receiptFileUrl } from './api'

/**
 * Preview of a stored capture.
 *
 * Download URLs cost a network round trip each, and they are fetched **only
 * once the card is near the viewport**. They were fetched on mount, which on
 * a screen holding fifty-odd receipts meant fifty-odd round trips before the
 * first one appeared — the lag Matt reported on his phone on 2026-10-19,
 * where most of those cards were several screens below the fold.
 *
 * Once fetched they stay cached for half an hour, so scrolling back up costs
 * nothing.
 *
 * PDFs render in an <iframe>, not an <img>. Every desktop browser has a built
 * -in PDF viewer; what it will not do is decode one into an image element,
 * which is what an earlier note here mistook for "browsers can't render these
 * inline". A forwarded booking confirmation is very often a PDF, so the
 * placeholder was hiding the most common emailed receipt there is.
 *
 * HEIC really does have no browser support and keeps its placeholder — though
 * captures are converted on upload now, so it should only appear on files
 * stored before that. Anything that fails to decode falls back to the same
 * placeholder rather than a browser broken-image icon.
 */
export function ReceiptThumbnail({ receipt }: { receipt: Receipt }) {
  const isPdf = receipt.originalFileUrl.endsWith('.pdf')
  const isHeic = /\.hei[cf]$/.test(receipt.originalFileUrl)
  const [failed, setFailed] = useState(false)
  const previewable = (isPdf || !isHeic) && !failed
  const frame = useRef<HTMLDivElement>(null)
  const near = useNearViewport(frame)

  const { data: url } = useQuery({
    queryKey: ['receiptUrl', receipt.originalFileUrl],
    queryFn: () => receiptFileUrl(receipt.originalFileUrl),
    enabled: previewable && near,
    staleTime: 30 * 60 * 1000,
  })

  if (!previewable) {
    return (
      <Placeholder
        label={isHeic ? 'iPhone photo' : 'No preview'}
        detail={isHeic ? 'Open it to read' : 'The file is stored safely'}
      />
    )
  }

  if (isPdf) {
    return (
      <div ref={frame} className="aspect-4/3 overflow-hidden rounded-lg bg-slate-100">
        {url !== undefined && (
          <iframe
            // #toolbar=0 drops the viewer's own controls, which are useless at
            // this size. pointer-events-none keeps the card clickable — an
            // iframe would otherwise swallow the click that opens the receipt.
            src={`${url}#toolbar=0&navpanes=0&view=FitH`}
            title={`Receipt captured ${formatDate(receipt.capturedAt)}`}
            className="pointer-events-none size-full border-0"
            loading="lazy"
          />
        )}
      </div>
    )
  }

  return (
    <div ref={frame} className="aspect-4/3 overflow-hidden rounded-lg bg-slate-100">
      {url !== undefined && (
        <img
          src={url}
          alt={`Receipt captured ${formatDate(receipt.capturedAt)}`}
          className="size-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  )
}

/**
 * True once the element has come within a screen of the viewport, and true
 * from then on — a card that scrolls away keeps its picture rather than
 * re-fetching when it comes back.
 *
 * Measured from the element's own rectangle rather than with an
 * `IntersectionObserver`, which looks like the obvious tool and has one fatal
 * property here: when it does not fire — a page that is not being composited,
 * an embedded view, a browser that throttles it — nothing loads at all and
 * the screen is quietly full of empty grey boxes. Verified: in the preview
 * pane used to test this, an observer on a plainly visible element never
 * called back once.
 *
 * A rectangle is always answerable. The cost is a scroll listener, which is
 * passive and does nothing but compare two numbers until it has said yes.
 */
function useNearViewport(ref: React.RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(false)

  useEffect(() => {
    if (near) return
    const element = ref.current
    if (element === null) return

    let timer: ReturnType<typeof setTimeout> | undefined
    const check = () => {
      timer = undefined
      const rect = element.getBoundingClientRect()
      // A screen's worth of margin either side, so the picture is there by the
      // time it is scrolled to rather than appearing under the thumb.
      const margin = window.innerHeight
      if (rect.top < window.innerHeight + margin && rect.bottom > -margin) setNear(true)
    }
    // Throttled on a timer rather than an animation frame. `requestAnimationFrame`
    // is the idiomatic choice and does not run at all when the page is not
    // being painted — measured in the preview pane, where it never fired once
    // and every card below the fold stayed blank however far it was scrolled.
    // A timer is less elegant and always runs.
    const onScroll = () => {
      if (timer === undefined) timer = setTimeout(check, 100)
    }

    check()
    // `true` captures scrolling in any ancestor, not only the window — the
    // receipts grid sits inside its own scrolling panel on some screens.
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      if (timer !== undefined) clearTimeout(timer)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [near, ref])

  return near
}

function Placeholder({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="flex aspect-4/3 items-center justify-center rounded-lg bg-slate-100 text-center">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="mt-0.5 text-xs text-slate-500">{detail}</p>
      </div>
    </div>
  )
}
